/**
 * Project Details Page
 * Comprehensive input forms for project assumptions
 */

import { useState } from 'react';
import { Card } from '@/components/common/Card';
import {
  Button,
  Input,
  Field,
  Dropdown,
  Option,
  Tab,
  TabList,
} from '@fluentui/react-components';
import {
  Play24Regular,
  Document24Regular,
} from '@fluentui/react-icons';
import { mockProjectInputs } from '@/services/mockData';
import { formatCurrency } from '@/utils/formatters';

export function ProjectDetails() {
  const [inputs, setInputs] = useState(mockProjectInputs);
  const [activeTab, setActiveTab] = useState('property');

  const handleInputChange = (field: string, value: string | number) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="project-details p-6">
      {/* Header */}
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold mb-2">Project Details</h1>
          <p className="text-gray-600">Configure project assumptions and inputs</p>
        </div>
        <div className="flex gap-3">
          <Button appearance="secondary" icon={<Document24Regular />}>
            Save Draft
          </Button>
          <Button appearance="primary" icon={<Play24Regular />}>
            Run Calculations
          </Button>
        </div>
      </header>

      {/* Tab Navigation */}
      <TabList
        selectedValue={activeTab}
        onTabSelect={(_, data) => setActiveTab(data.value as string)}
        className="mb-6"
      >
        <Tab value="property">Property Info</Tab>
        <Tab value="timeline">Timeline</Tab>
        <Tab value="costs">Development Costs</Tab>
        <Tab value="financing">Financing</Tab>
        <Tab value="operations">Operations</Tab>
        <Tab value="partnership">Partnership</Tab>
      </TabList>

      {/* Property Information Tab */}
      {activeTab === 'property' && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-6">Investment Description</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Property Name">
              <Input
                value={inputs.propertyName}
                onChange={(e) => handleInputChange('propertyName', e.target.value)}
              />
            </Field>

            <Field label="Address">
              <Input
                value={inputs.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
              />
            </Field>

            <Field label="City">
              <Input
                value={inputs.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
              />
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="State">
                <Input
                  value={inputs.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  maxLength={2}
                />
              </Field>
              <Field label="Zip Code">
                <Input
                  value={inputs.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                />
              </Field>
            </div>

            <Field label="Retail Type">
              <Dropdown
                value={inputs.retailType}
                onOptionSelect={(_, data) => handleInputChange('retailType', data.optionValue || '')}
              >
                <Option value="Community Center">Community Center</Option>
                <Option value="Neighborhood Center">Neighborhood Center</Option>
                <Option value="Power Center">Power Center</Option>
                <Option value="Lifestyle Center">Lifestyle Center</Option>
              </Dropdown>
            </Field>

            <Field label="Total NRA (SF)">
              <Input
                type="number"
                value={inputs.totalNRA.toString()}
                onChange={(e) => handleInputChange('totalNRA', Number(e.target.value))}
              />
            </Field>

            <Field label="Analysis Start Date">
              <Input
                type="date"
                value={inputs.analysisStartDate}
                onChange={(e) => handleInputChange('analysisStartDate', e.target.value)}
              />
            </Field>
          </div>
        </Card>
      )}

      {/* Timeline Tab */}
      {activeTab === 'timeline' && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-6">Development Timeline</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Construction Start (Month)">
              <Input
                type="number"
                value={inputs.constructionStartMonth.toString()}
                onChange={(e) => handleInputChange('constructionStartMonth', Number(e.target.value))}
              />
            </Field>

            <Field label="Construction End (Month)">
              <Input
                type="number"
                value={inputs.constructionEndMonth.toString()}
                onChange={(e) => handleInputChange('constructionEndMonth', Number(e.target.value))}
              />
            </Field>

            <Field label="Lease-Up Start (Month)">
              <Input
                type="number"
                value={inputs.leaseUpStartMonth.toString()}
                onChange={(e) => handleInputChange('leaseUpStartMonth', Number(e.target.value))}
              />
            </Field>

            <Field label="Stabilization Month">
              <Input
                type="number"
                value={inputs.stabilizationMonth.toString()}
                onChange={(e) => handleInputChange('stabilizationMonth', Number(e.target.value))}
              />
            </Field>

            <Field label="Reversion/Exit Month">
              <Input
                type="number"
                value={inputs.reversionMonth.toString()}
                onChange={(e) => handleInputChange('reversionMonth', Number(e.target.value))}
              />
            </Field>
          </div>

          {/* Timeline Visualization */}
          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between text-sm">
              <div className="text-center">
                <div className="font-semibold">Construction</div>
                <div className="text-gray-600">Months 1-18</div>
              </div>
              <div className="flex-1 h-2 bg-blue-200 mx-4 rounded"></div>
              <div className="text-center">
                <div className="font-semibold">Lease-Up</div>
                <div className="text-gray-600">Months 12-30</div>
              </div>
              <div className="flex-1 h-2 bg-green-200 mx-4 rounded"></div>
              <div className="text-center">
                <div className="font-semibold">Stabilized</div>
                <div className="text-gray-600">Months 30-60</div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Development Costs Tab */}
      {activeTab === 'costs' && (
        <div className="space-y-6">
          {/* Land Costs */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Land Costs</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Land Acquisition">
                <Input
                  type="number"
                  value={inputs.landAcquisition.toString()}
                  onChange={(e) => handleInputChange('landAcquisition', Number(e.target.value))}
                  contentBefore="$"
                />
              </Field>
              <Field label="Closing & Legal Costs">
                <Input
                  type="number"
                  value={inputs.landClosingCosts.toString()}
                  onChange={(e) => handleInputChange('landClosingCosts', Number(e.target.value))}
                  contentBefore="$"
                />
              </Field>
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded">
              <span className="font-semibold">Total Land Costs:</span>{' '}
              {formatCurrency(inputs.landAcquisition + inputs.landClosingCosts)}
            </div>
          </Card>

          {/* Hard Costs */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Hard Costs</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Construction Cost ($/SF)">
                <Input
                  type="number"
                  value={inputs.hardCostsPerSF.toString()}
                  onChange={(e) => handleInputChange('hardCostsPerSF', Number(e.target.value))}
                  contentBefore="$"
                />
              </Field>
              <Field label="S-Curve Steepness">
                <Input
                  type="number"
                  value={inputs.sCurveSteepness.toString()}
                  onChange={(e) => handleInputChange('sCurveSteepness', Number(e.target.value))}
                />
              </Field>
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded">
              <span className="font-semibold">Total Hard Costs:</span>{' '}
              {formatCurrency(inputs.hardCostsPerSF * inputs.totalNRA)}
            </div>
          </Card>

          {/* Soft Costs */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Soft Costs</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Architectural & Engineering">
                <Input
                  type="number"
                  value={inputs.architecturalEngineering.toString()}
                  onChange={(e) => handleInputChange('architecturalEngineering', Number(e.target.value))}
                  contentBefore="$"
                />
              </Field>
              <Field label="Development Fee">
                <Input
                  type="number"
                  value={inputs.developmentFee.toString()}
                  onChange={(e) => handleInputChange('developmentFee', Number(e.target.value))}
                  contentBefore="$"
                />
              </Field>
              <Field label="Legal & Accounting">
                <Input
                  type="number"
                  value={inputs.legalAccounting.toString()}
                  onChange={(e) => handleInputChange('legalAccounting', Number(e.target.value))}
                  contentBefore="$"
                />
              </Field>
              <Field label="Permits & Fees">
                <Input
                  type="number"
                  value={inputs.permits.toString()}
                  onChange={(e) => handleInputChange('permits', Number(e.target.value))}
                  contentBefore="$"
                />
              </Field>
              <Field label="Insurance (Construction)">
                <Input
                  type="number"
                  value={inputs.insurance.toString()}
                  onChange={(e) => handleInputChange('insurance', Number(e.target.value))}
                  contentBefore="$"
                />
              </Field>
              <Field label="Contingency">
                <Input
                  type="number"
                  value={inputs.contingency.toString()}
                  onChange={(e) => handleInputChange('contingency', Number(e.target.value))}
                  contentBefore="$"
                />
              </Field>
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded">
              <span className="font-semibold">Total Soft Costs:</span>{' '}
              {formatCurrency(
                inputs.architecturalEngineering +
                inputs.developmentFee +
                inputs.legalAccounting +
                inputs.permits +
                inputs.insurance +
                inputs.contingency
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Financing Tab */}
      {activeTab === 'financing' && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-6">Financing Structure</h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Construction Loan</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Loan to Construction (%)">
                  <Input
                    type="number"
                    value={(inputs.loanToConstruction * 100).toString()}
                    onChange={(e) => handleInputChange('loanToConstruction', Number(e.target.value) / 100)}
                    contentAfter="%"
                  />
                </Field>
                <Field label="Interest Rate (%)">
                  <Input
                    type="number"
                    value={(inputs.constructionInterestRate * 100).toString()}
                    onChange={(e) => handleInputChange('constructionInterestRate', Number(e.target.value) / 100)}
                    contentAfter="%"
                  />
                </Field>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Permanent Loan</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Loan Amount">
                  <Input
                    type="number"
                    value={inputs.permanentLoanAmount.toString()}
                    onChange={(e) => handleInputChange('permanentLoanAmount', Number(e.target.value))}
                    contentBefore="$"
                  />
                </Field>
                <Field label="Interest Rate (%)">
                  <Input
                    type="number"
                    value={(inputs.permanentInterestRate * 100).toString()}
                    onChange={(e) => handleInputChange('permanentInterestRate', Number(e.target.value) / 100)}
                    contentAfter="%"
                  />
                </Field>
                <Field label="Amortization (Years)">
                  <Input
                    type="number"
                    value={inputs.permanentAmortization.toString()}
                    onChange={(e) => handleInputChange('permanentAmortization', Number(e.target.value))}
                  />
                </Field>
                <Field label="Origination Fee (%)">
                  <Input
                    type="number"
                    value={(inputs.loanOriginationFee * 100).toString()}
                    onChange={(e) => handleInputChange('loanOriginationFee', Number(e.target.value) / 100)}
                    contentAfter="%"
                  />
                </Field>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Operations Tab */}
      {activeTab === 'operations' && (
        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Operating Assumptions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Vacancy Rate (%)">
                <Input
                  type="number"
                  value={(inputs.vacancyRate * 100).toString()}
                  onChange={(e) => handleInputChange('vacancyRate', Number(e.target.value) / 100)}
                  contentAfter="%"
                />
              </Field>
              <Field label="Management Fee (%)">
                <Input
                  type="number"
                  value={(inputs.managementFeeRate * 100).toString()}
                  onChange={(e) => handleInputChange('managementFeeRate', Number(e.target.value) / 100)}
                  contentAfter="%"
                />
              </Field>
              <Field label="Property Tax Rate (%)">
                <Input
                  type="number"
                  value={(inputs.propertyTaxRate * 100).toString()}
                  onChange={(e) => handleInputChange('propertyTaxRate', Number(e.target.value) / 100)}
                  contentAfter="%"
                />
              </Field>
              <Field label="Insurance ($/SF)">
                <Input
                  type="number"
                  value={inputs.insurancePerSF.toString()}
                  onChange={(e) => handleInputChange('insurancePerSF', Number(e.target.value))}
                  contentBefore="$"
                />
              </Field>
              <Field label="Maintenance ($/SF)">
                <Input
                  type="number"
                  value={inputs.maintenancePerSF.toString()}
                  onChange={(e) => handleInputChange('maintenancePerSF', Number(e.target.value))}
                  contentBefore="$"
                />
              </Field>
              <Field label="Utilities ($/SF)">
                <Input
                  type="number"
                  value={inputs.utilitiesPerSF.toString()}
                  onChange={(e) => handleInputChange('utilitiesPerSF', Number(e.target.value))}
                  contentBefore="$"
                />
              </Field>
              <Field label="Marketing Budget (Annual)">
                <Input
                  type="number"
                  value={inputs.marketingBudget.toString()}
                  onChange={(e) => handleInputChange('marketingBudget', Number(e.target.value))}
                  contentBefore="$"
                />
              </Field>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Reversion Assumptions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Exit Cap Rate (%)">
                <Input
                  type="number"
                  value={(inputs.exitCapRate * 100).toString()}
                  onChange={(e) => handleInputChange('exitCapRate', Number(e.target.value) / 100)}
                  contentAfter="%"
                />
              </Field>
              <Field label="Selling Costs (%)">
                <Input
                  type="number"
                  value={(inputs.sellingCosts * 100).toString()}
                  onChange={(e) => handleInputChange('sellingCosts', Number(e.target.value) / 100)}
                  contentAfter="%"
                />
              </Field>
              <Field label="Broker Commission (%)">
                <Input
                  type="number"
                  value={(inputs.brokerCommission * 100).toString()}
                  onChange={(e) => handleInputChange('brokerCommission', Number(e.target.value) / 100)}
                  contentAfter="%"
                />
              </Field>
            </div>
          </Card>
        </div>
      )}

      {/* Partnership Tab */}
      {activeTab === 'partnership' && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-6">Partnership Structure</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="LP Ownership (%)">
              <Input
                type="number"
                value={(inputs.lpPercentage * 100).toString()}
                onChange={(e) => handleInputChange('lpPercentage', Number(e.target.value) / 100)}
                contentAfter="%"
              />
            </Field>
            <Field label="GP Ownership (%)">
              <Input
                type="number"
                value={(inputs.gpPercentage * 100).toString()}
                onChange={(e) => handleInputChange('gpPercentage', Number(e.target.value) / 100)}
                contentAfter="%"
              />
            </Field>
            <Field label="Preferred Return (%)">
              <Input
                type="number"
                value={(inputs.preferredReturn * 100).toString()}
                onChange={(e) => handleInputChange('preferredReturn', Number(e.target.value) / 100)}
                contentAfter="%"
              />
            </Field>
            <Field label="GP Promote Tier 1 (%)">
              <Input
                type="number"
                value={(inputs.gpPromoteTier1 * 100).toString()}
                onChange={(e) => handleInputChange('gpPromoteTier1', Number(e.target.value) / 100)}
                contentAfter="%"
              />
            </Field>
            <Field label="GP Promote Tier 2 (%)">
              <Input
                type="number"
                value={(inputs.gpPromoteTier2 * 100).toString()}
                onChange={(e) => handleInputChange('gpPromoteTier2', Number(e.target.value) / 100)}
                contentAfter="%"
              />
            </Field>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold mb-2">Waterfall Structure</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm">
              <li>Return of Capital to LP/GP (90%/10%)</li>
              <li>Preferred Return of {(inputs.preferredReturn * 100).toFixed(1)}% to LP</li>
              <li>GP Catch-up to {(inputs.gpPromoteTier1 * 100).toFixed(0)}%</li>
              <li>Remaining split {(100 - inputs.gpPromoteTier1 * 100).toFixed(0)}%/{(inputs.gpPromoteTier1 * 100).toFixed(0)}% LP/GP</li>
            </ol>
          </div>
        </Card>
      )}
    </div>
  );
}
